/**
 * BulkAddPasteForm — Stage 1 of the bulk-add flow (Block 6.E).
 *
 * Textarea + format hint + "Parse + Preview" button. Mobile-first per
 * ADR-017 F8: full-width textarea at 375px; no CSV-upload-only path.
 */

import { useState, type ReactNode } from 'react';

export interface BulkAddPasteFormProps {
  initialPaste?: string;
  busy?: boolean;
  errorMessage?: string | null;
  onParse: (paste: string) => void;
}

export function BulkAddPasteForm({
  initialPaste = '',
  busy = false,
  errorMessage = null,
  onParse,
}: BulkAddPasteFormProps): ReactNode {
  const [paste, setPaste] = useState(initialPaste);

  return (
    <div className="space-y-3">
      <div className="text-xs text-ink-soft leading-relaxed">
        Paste rows from Excel / Sheets, one organization per line:
        <pre className="my-2 px-3 py-2 bg-sand border border-sage-800/[8%] rounded text-[11px] overflow-x-auto whitespace-pre">
{`name<TAB>phone<TAB>street<TAB>line 2<TAB>city<TAB>state<TAB>ZIP<TAB>category<TAB>subcategories<TAB>service-areas<TAB>kind`}
        </pre>
        <ul className="list-disc pl-5 space-y-0.5">
          <li>The address is five columns: street, line 2, city, state and ZIP. A city and a state with no street is fine; leave all five blank for no address.</li>
          <li>The last column <span className="font-mono">kind</span> is optional; defaults to <span className="font-mono">other</span> when blank.</li>
          <li>Allowed kinds: <span className="font-mono">agency, firm, government-office, individual-practitioner, other</span>.</li>
          <li>Multiple subcategories or service-areas separated by <span className="font-mono">|</span> within the field.</li>
        </ul>
      </div>

      <textarea
        value={paste}
        onChange={(e) => setPaste(e.target.value)}
        rows={10}
        aria-label="Bulk-add paste textarea"
        placeholder="Acme Care\t555-1234\t123 Main St\t\tSpringfield\t\t\tcare-providers\tcare-management\t\tfirm"
        disabled={busy}
        className="w-full min-h-44 px-3 py-2 border border-sage-400/30 rounded-md text-sm font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
      />

      {errorMessage && (
        <div role="alert" className="text-xs text-red-700" data-testid="bulk-add-paste-error">
          ⚠ {errorMessage}
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => onParse(paste)}
          disabled={busy || paste.trim().length === 0}
          className="min-h-11 px-4 py-2 text-sm bg-sage-800 text-cream rounded-md hover:bg-forest-700 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        >
          {busy ? 'Parsing…' : 'Parse + Preview →'}
        </button>
      </div>
    </div>
  );
}
